// React & Hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// API & Types
import { getTireAPI } from "services/api/calculations/tire/api";
import { initialTireDetailForm } from "services/api/calculations/tire/consts";
import { TireDetailFormValues } from "services/api/calculations/tire/types";
import { Errors } from "services/forms/validation/types";

// UI Components
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Button } from "ui/components/Button/Button";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";

// Project Specific Components
import TireDetailCard from "./components/TireDetailCard/TireDetailCard";
import TireDeleteCard from "./components/TireDeleteCard/TireDeleteCard";

// Helpers & Utilities
import { transformRoute, useTranslate } from "helpers";

// Constants
import { CALCULATION_TIRE_DETAIL_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { SIDEBAR_ITEM_CALCULATION_TIRE } from "app/components/Sidebar/consts";

// Types
import { TireDetailPageCmp, TireDetailPageProps } from "./types";
import { Routes } from "ui/modules/calculation/navigation";

const TireDetailPage: TireDetailPageCmp = (props: TireDetailPageProps) => {
  const { tireId } = useParams();

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<TireDetailFormValues>(
    initialTireDetailForm
  );

  const navigate = useNavigate();

  const title = CALCULATION_TIRE_DETAIL_PAGE_TITLE;

  useEffect(() => {
    if (tireId) fetchTire(tireId);
  }, [tireId]);

  const fetchTire = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialTireDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getTireAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const breadcrumbs: BreadcrumbItem[] = [
    {
      text: SIDEBAR_ITEM_CALCULATION_TIRE.text,
      route: SIDEBAR_ITEM_CALCULATION_TIRE.route,
    },
  ];

  const handleDeleteSuccess = useCallback(() => {
    navigate(
      transformRoute(Routes.CalculationTireSearchPageRouter, {
        tireId,
      })
    );
  }, []);

  if (!tireId) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white border-b border-slate-200 rounded-t-md">
          <PageHeader>
            <PageTitle
              title={`${t(title.text)} [${formValues.width}/${
                formValues.height
              }/${formValues.radius}]`}
              icon={title.icon}
              iconColor={title.iconColor}
            />
            <div className="flex justify-between">
              <Breadcrumb items={breadcrumbs} />
              <div className="flex gap-2">
                <Button
                  size="large"
                  variant="remove"
                  children={t("admin.calculation.setting.tire.detail.delete")}
                  onClick={() => setShowDeleteModal(true)}
                  startIcon="/media/icons/trash.svg"
                />
              </div>
            </div>
          </PageHeader>
        </div>
        <div className="px-4 py-6  lg:px-4">
          <TireDetailCard tireId={tireId} />
        </div>
      </div>
      {showDeleteModal && (
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <ModalHeader
            title={t("admin.calculation.setting.tire.delete.title")}
            onHide={() => setShowDeleteModal(false)}
          />
          <ModalBody>
            <TireDeleteCard tireId={tireId} onSuccess={handleDeleteSuccess} />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

TireDetailPage.route = Routes.CalculationTireDetailPageRouter;

export default TireDetailPage;
